import type {
  DeleteIdsResponse,
  PagedResponse,
  PaginationParams,
  PlainObject,
  SearchableProps,
  Timestamp,
  Timestamps,
} from './common';
import type { ConnectyCubeUsers } from './users';

export const DialogType = {
  PUBLIC: 1,
  GROUP: 2,
  PRIVATE: 3,
  BROADCAST: 4,
  MEETING: 5,
} as const;
export type DialogType = (typeof DialogType)[keyof typeof DialogType];

export const LastMessageMessageStatus = {
  SENT: 'sent',
  DELIVERED: 'delivered',
  READ: 'read',
} as const;
export type LastMessageMessageStatus = (typeof LastMessageMessageStatus)[keyof typeof LastMessageMessageStatus];

export const DialogDateProp = {
  CREATED: 'created_at',
  UPDATED: 'updated_at',
  LAST_MESSAGE: 'last_message_date_sent',
} as const;
export type DialogDateProp = (typeof DialogDateProp)[keyof typeof DialogDateProp];

export namespace ConnectyCubeDialogs {
  export type Dialog<ExtensionExtra = PlainObject> = Timestamps & {
    _id: string;
    name: string;
    description: string | null;
    photo: string | null;
    type: DialogType;
    user_id: number;
    xmpp_room_jid?: string | null;
    last_message: string | null;
    last_message_date_sent?: Timestamp;
    last_message_id: string | null;
    last_message_user_id: number | null;
    last_message_status: LastMessageMessageStatus | string | null;
    occupants_ids: number[];
    admins_ids: number[];
    occupants_count?: number;
    pinned_messages_ids?: string[];
    unread_messages_count: number;
    extensions?: ExtensionExtra;
    permissions?: { allow_preview: boolean | string | number } | null;
  };
  // Request parameters type
  export type ListParams = SearchableProps<Dialog> & Omit<PaginationParams<Dialog>, 'offset'> & { count?: number };
  export type CreateParams<ExtensionExtra = PlainObject> = Pick<
    Dialog<ExtensionExtra>,
    'type' | 'extensions' | 'permissions' | 'pinned_messages_ids'
  > & {
    name?: string;
    photo?: string | null;
    description?: string | null;
    occupants_ids?: (string | number)[] | string;
    admins_ids?: (string | number)[] | string;
  };
  export type UpdateParams<ExtensionExtra = PlainObject> = Omit<
    CreateParams<ExtensionExtra>,
    'type' | 'occupants_ids' | 'admins_ids'
  > & {
    push_all?: Pick<CreateParams<ExtensionExtra>, 'occupants_ids' | 'admins_ids' | 'pinned_messages_ids'>;
    pull_all?: Pick<CreateParams<ExtensionExtra>, 'occupants_ids' | 'admins_ids' | 'pinned_messages_ids'>;
  };
  export type GetPublicOccupantsParams = Pick<PaginationParams, 'skip' | 'limit'>;
  // Response data types
  export type ListResponse = PagedResponse<Dialog>;
  export type DeleteResponse = DeleteIdsResponse;
  export type NotificationsSettingsResponse = { notifications: { enabled: number | string } };
  export type GetNotificationsSettingsResponse = NotificationsSettingsResponse;
  export type UpdateNotificationsSettingsResponse = NotificationsSettingsResponse;
  export type GetPublicOccupantsResponse = { items: ConnectyCubeUsers.User[] };
}
